import { useState, useEffect } from 'react';
import { publicApiCall } from '../services/api';

/**
 * Branding (custom logo) shared across the app.
 *
 * The logo is served publicly through CloudFront at a stable same-domain URL
 * (https://<domain>/branding/logo.png). This hook fetches that URL from the
 * public /auth/branding endpoint so the authenticated dashboard can render it.
 *
 * A module-level cache + in-flight promise means the endpoint is hit at most
 * once per page load no matter how many components call the hook. A subscriber
 * registry lets refreshBranding() re-fetch and push the result to every mounted
 * consumer, so a newly uploaded logo appears without a full page reload.
 */
export interface Branding {
  logoUrl: string;
  logoConfigured: boolean;
}

// Default logo path — same-origin route served by the /branding/* CloudFront
// behavior. Used as the fallback when no custom logo has been uploaded
// (logoConfigured=false) or when the API call fails. NOTE: no default image ships
// at this path, so consumers must gate rendering on logoConfigured before using it.
const DEFAULT_BRANDING: Branding = {
  logoUrl: '/branding/logo.png',
  logoConfigured: false,
};

let cachedBranding: Branding | null = null;
let inFlight: Promise<Branding> | null = null;

// Mounted consumers' setState callbacks. refreshBranding() pushes fresh data here.
const subscribers = new Set<(b: Branding) => void>();

async function fetchBranding(): Promise<Branding> {
  if (cachedBranding) return cachedBranding;
  if (inFlight) return inFlight;

  inFlight = publicApiCall('/auth/branding')
    .then((data: Partial<Branding>) => {
      const branding: Branding = {
        logoUrl: data?.logoUrl || DEFAULT_BRANDING.logoUrl,
        logoConfigured: Boolean(data?.logoConfigured),
      };
      cachedBranding = branding;
      return branding;
    })
    .catch((error) => {
      console.error('Failed to load branding, using default:', error);
      return DEFAULT_BRANDING;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Clears the cache, re-fetches, and notifies every mounted consumer so the UI
 * reflects a newly uploaded logo without a reload. Call this after an admin upload.
 */
export function refreshBranding() {
  cachedBranding = null;
  fetchBranding().then((branding) => {
    subscribers.forEach((notify) => notify(branding));
  });
}

export function useBranding() {
  const [branding, setBranding] = useState<Branding>(cachedBranding || DEFAULT_BRANDING);
  const [loading, setLoading] = useState<boolean>(!cachedBranding);

  useEffect(() => {
    let active = true;

    // Subscribe so refreshBranding() can push updates to this component.
    subscribers.add(setBranding);

    fetchBranding()
      .then((result) => {
        if (active) {
          setBranding(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      subscribers.delete(setBranding);
    };
  }, []);

  return { branding, loading };
}
